'use client'

import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { DM_Sans, IBM_Plex_Mono } from 'next/font/google'
import { Blocks, Bot, ClipboardCheck } from 'lucide-react'
import HBPlaybook, { PlaybookTabCard } from '@/components/home-builders/HBPlaybook'
import HBWidgetLibrary from '@/components/home-builders/HBWidgetLibrary'
import { getBuilderWidgetCount } from '@/lib/builder-widget-catalog'
import { HB_PLAYBOOK_BUY_PATH } from '@/lib/home-builder-playbook'
import './hb-services.css'

const dmSans = DM_Sans({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
})

const plexMono = IBM_Plex_Mono({
  subsets: ['latin'],
  weight: ['400', '500', '600'],
})

type TrackId = 'playbook' | 'widgets' | 'systems'

interface TrackItem {
  t: string
  d: string
}

interface Track {
  id: TrackId
  name: string
  productName?: string
  short?: string
  lead?: ReactNode
  items?: TrackItem[]
  note?: string
  cta?: { label: string; href: string }
  cardCta?: string
  cardCtaHref?: string
  cardTags?: string[]
}

function QuotePill({ children }: { children: string }) {
  return (
    <a
      href="#get-quote"
      className="svc-pill"
      onClick={(event) => {
        event.preventDefault()
        document.getElementById('get-quote')?.scrollIntoView({ behavior: 'smooth' })
      }}
    >
      {children}
    </a>
  )
}

function BuilderOfferCards() {
  return (
    <div className={`svc-pricing-grid svc-pricing-grid--3 ${dmSans.className}`}>
      <article className="svc-card">
        <div className="svc-card-body">
          <div className="svc-card-heading">
            <div className="svc-card-titleblock">
              <span className="svc-card-icon" aria-hidden>
                <ClipboardCheck size={30} strokeWidth={1.7} />
              </span>
              <div>
                <h3>Audit</h3>
                <p className="svc-card-sub">Fixed-price</p>
              </div>
            </div>
          </div>
          <p className="svc-card-copy">
            A fixed-price audit of your whole lead engine: ads, SEO, community pages, lead flows, CRM, and tracking. See where buyers drop off, and what it costs to fix.
          </p>
          <ul className="svc-checks">
            <li>Every channel reviewed, from first click to contract</li>
            <li>Findings ranked by impact on sales</li>
            <li>What to fix first</li>
            <li>Fixed-price quote for the work</li>
            <li>$500 credit toward services</li>
          </ul>
        </div>
        <div className="svc-card-footer">
          <QuotePill>Get in touch</QuotePill>
        </div>
      </article>

      <article className="svc-card svc-card--priority">
        <div className="svc-card-body">
          <div className="svc-card-heading">
            <div className="svc-card-titleblock">
              <span className="svc-card-icon" aria-hidden>
                <Blocks size={30} strokeWidth={1.7} />
              </span>
              <div>
                <h3>Build</h3>
                <p className="svc-card-sub">Marketing & CRM</p>
              </div>
            </div>
          </div>
          <p className="svc-card-copy">
            CRM, tracking, and ads built around how builders sell homes.
          </p>
          <ul className="svc-checks">
            <li>Ads and SEO for your communities</li>
            <li>CRM for your sales team</li>
            <li>Lead source tracked by community</li>
            <li>Work directly with me</li>
            <li>Quote in a day</li>
          </ul>
        </div>
        <div className="svc-card-footer">
          <QuotePill>Get in touch</QuotePill>
        </div>
      </article>

      <article className="svc-card">
        <div className="svc-card-body">
          <div className="svc-card-heading">
            <div className="svc-card-titleblock">
              <span className="svc-card-icon" aria-hidden>
                <Bot size={30} strokeWidth={1.7} />
              </span>
              <div>
                <h3>Automate</h3>
                <p className="svc-card-sub">AI agents & flows</p>
              </div>
            </div>
          </div>
          <p className="svc-card-copy">
            Agents that answer buyer inquiries and follow up until the appointment is booked.
          </p>
          <ul className="svc-checks">
            <li>Every inquiry answered in seconds</li>
            <li>Follow-up until the visit is set</li>
            <li>More contracts from current leads</li>
            <li>Connects to your CRM and site</li>
            <li>Live in days</li>
          </ul>
        </div>
        <div className="svc-card-footer">
          <QuotePill>Get in touch</QuotePill>
        </div>
      </article>
    </div>
  )
}

function tracksFor(_city?: string): Track[] {
  return [
    {
      id: 'systems',
      name: 'Remote & On-Site',
      productName: 'Systems & Marketing Services for Builders',
      cardCta: 'Get Started',
      cardCtaHref: '#get-quote',
      cardTags: ['Home builder services', 'Projects or retainer'],
      short: 'CRM, attribution, and integrations, then ads and SEO on top. Work with me remotely or on-site so every inquiry gets caught, ranked, and followed up.',
    },
    {
      id: 'playbook',
      name: 'Ready to install',
    },
    {
      id: 'widgets',
      name: `${getBuilderWidgetCount()} lead-gen widgets`,
      productName: 'Home Builder Widgets',
      cardCta: 'Browse Widgets',
      cardCtaHref: '#hb-panel-widgets',
      cardTags: ['CRM-connected', 'Your branding', 'Unlimited traffic'],
      short: 'Calculators, quizzes and recommenders that capture buyer data before visitors leave your site.',
      lead: <>Most builder websites are brochures. Widgets make them <em>ask questions.</em></>,
    },
  ]
}

function ArrowIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M14 5l7 7-7 7M21 12H3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function GridIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1.2" />
      <rect x="14" y="3" width="7" height="7" rx="1.2" />
      <rect x="3" y="14" width="7" height="7" rx="1.2" />
      <rect x="14" y="14" width="7" height="7" rx="1.2" />
    </svg>
  )
}

const CARD_CTA_ICON: Partial<Record<TrackId, () => ReactNode>> = {
  widgets: GridIcon,
  systems: ArrowIcon,
}

function MiniWidgets() {
  return (
    <img
      className="mini"
      src="/images/widgets/widget-cropped.jpg"
      alt="Home builder lead generation widgets"
    />
  )
}

function MiniSystems() {
  return (
    <img
      className="mini"
      src="/images/crm.png"
      alt="Home builder CRM and pipeline dashboard"
    />
  )
}

const MINI: Partial<Record<TrackId, () => ReactNode>> = {
  widgets: MiniWidgets,
  systems: MiniSystems,
}

function TrackPanel({
  tracks,
  index,
}: {
  tracks: Track[]
  index: number
}) {
  const s = tracks[index]

  if (s.id === 'playbook') {
    return <HBPlaybook />
  }

  if (s.id === 'widgets') {
    return (
      <div
        className={`panel panel-widgets panel-attach-${s.id}`}
        role="tabpanel"
        id={`hb-panel-${s.id}`}
        aria-labelledby={`hb-tab-${s.id}`}
      >
        <HBWidgetLibrary lead={s.lead} />
      </div>
    )
  }

  if (s.id === 'systems') {
    return (
      <div
        className={`panel panel-svc panel-attach-${s.id}`}
        role="tabpanel"
        id={`hb-panel-${s.id}`}
        aria-labelledby={`hb-tab-${s.id}`}
      >
        <BuilderOfferCards />
      </div>
    )
  }

  return null
}

interface HBServicesProps {
  clientLocation?: string
}

export default function HBServices({ clientLocation }: HBServicesProps) {
  const tracks = tracksFor(clientLocation)
  const [cur, setCur] = useState(0)
  const [narrow, setNarrow] = useState(false)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash
      const fromPath = hash.match(/^#path-(playbook|widgets|systems)$/)
      const fromPanel = hash.match(/^#hb-panel-(playbook|widgets|systems)$/)
      const playbookBuy = hash === '#playbook-buy'
      if (playbookBuy) {
        window.location.replace(HB_PLAYBOOK_BUY_PATH)
        return
      }
      const id = fromPath?.[1] ?? fromPanel?.[1] ?? null
      if (!id) return
      const i = tracks.findIndex(t => t.id === id)
      if (i < 0) return
      setCur(i)
      if (fromPath || fromPanel) {
        const scrollId = id === 'playbook' ? 'hb-card-playbook' : `hb-panel-${id}`
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.getElementById(scrollId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          })
        })
      }
    }
    applyHash()
    window.addEventListener('hashchange', applyHash)
    return () => window.removeEventListener('hashchange', applyHash)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1040px)')
    const onChange = () => setNarrow(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const select = (i: number, focus = false) => {
    setCur(i)
    if (focus) tabRefs.current[i]?.focus()
  }

  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!(e.target instanceof HTMLElement) || !e.target.closest('.c')) return
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const next = e.key === 'ArrowRight' ? (cur + 1) % tracks.length : (cur + tracks.length - 1) % tracks.length
    select(next, true)
  }

  const goToCardCta = (e: MouseEvent<HTMLAnchorElement>, i: number) => {
    e.preventDefault()
    e.stopPropagation()
    select(i)
  }

  const panel = (
    <TrackPanel
      tracks={tracks}
      index={cur}
    />
  )

  return (
    <section
      id="what-we-do"
      className="hb-svc"
      style={{ ['--hb-mono' as string]: plexMono.style.fontFamily }}
      aria-label="How builders work with me"
    >
      <div className="grid-bg" />
      <div className="wrap">
        <div className="head">
          <h2 className="sec">How builders work with me.</h2>
          <p className="sub">Done-for-you services, powerful playbooks, and lead-gen widgets for your site.</p>
        </div>

        <div className="stage">
        <div className="cards" role="tablist" aria-label="Service tracks" onKeyDown={onTabKey}>
          {tracks.map((s, i) => {
            const Mini = MINI[s.id]
            const CtaIcon = CARD_CTA_ICON[s.id]
            const selected = i === cur
            return (
              <Fragment key={s.id}>
                <div
                  id={s.id === 'playbook' ? 'hb-card-playbook' : undefined}
                  className={`c${selected ? ' is-on' : ''}${s.id === 'playbook' ? ' c-playbook' : ''}${s.cardCta ? ' c-offer' : ''}`}
                >
                  {s.id === 'playbook' ? (
                    <PlaybookTabCard
                      selected={selected}
                      tabRef={el => { tabRefs.current[i] = el }}
                      onSelect={() => select(i)}
                    />
                  ) : (
                    <>
                      <button
                        ref={el => { tabRefs.current[i] = el }}
                        type="button"
                        className="c-hit"
                        role="tab"
                        id={`hb-tab-${s.id}`}
                        aria-selected={selected}
                        aria-controls={`hb-panel-${s.id}`}
                        aria-labelledby={`hb-tab-label-${s.id}`}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => select(i)}
                      >
                        <div className="thumb">{Mini ? <Mini /> : null}</div>
                      </button>
                      <div className="c-copy" onClick={() => select(i)}>
                        {s.productName ? (
                          <>
                            <h3 id={`hb-tab-label-${s.id}`}>{s.productName}</h3>
                            <div className="c-series">{s.name}</div>
                          </>
                        ) : (
                          <h3 id={`hb-tab-label-${s.id}`}>{s.name}</h3>
                        )}
                        {s.short ? <p>{s.short}</p> : null}
                      </div>
                      {selected && s.cardTags?.length ? (
                        <div className="c-tags">
                          {s.cardTags.map(tag => (
                            <span key={tag}>{tag}</span>
                          ))}
                        </div>
                      ) : null}
                      {s.cardCta && !selected ? (
                        <a
                          className="c-cta"
                          href={s.cardCtaHref ?? `#hb-panel-${s.id}`}
                          onClick={e => goToCardCta(e, i)}
                        >
                          {CtaIcon ? <CtaIcon /> : null}
                          {s.cardCta}
                        </a>
                      ) : null}
                    </>
                  )}
                </div>
                {narrow && selected ? panel : null}
              </Fragment>
            )
          })}
        </div>
        {!narrow ? panel : null}
        </div>
      </div>
    </section>
  )
}
